import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Bookmark, BookmarkPlus, Trash2, Loader2 } from "lucide-react";

/**
 * Saved filter presets menu (per user, per page, per workspace).
 * currentFilters: object snapshot of the page's active filters
 * onApply(filters): callback to restore a preset
 */
export default function PresetsMenu({ pageKey, currentFilters, onApply }) {
  const [presets, setPresets] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/presets", { params: { page_key: pageKey } })
      .then((res) => setPresets(res.data))
      .catch(() => {});
  }, [pageKey]);

  useEffect(load, [load]);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Please enter a preset name.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/presets", { page_key: pageKey, name: name.trim(), filters: currentFilters });
      toast.success(`Preset "${name.trim()}" saved`);
      setSaveOpen(false);
      setName("");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to save preset"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e, preset) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.delete(`/presets/${preset.id}`);
      toast.success(`Preset "${preset.name}" deleted`);
      load();
    } catch {
      toast.error("Failed to delete preset");
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="rounded-lg" data-testid={`${pageKey}-presets-button`}>
            <Bookmark className="h-4 w-4" />
            <span className="hidden sm:inline ml-1.5">Presets</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">Saved presets</DropdownMenuLabel>
          {presets.length === 0 ? (
            <DropdownMenuItem disabled className="text-muted-foreground text-xs">
              No saved presets yet
            </DropdownMenuItem>
          ) : (
            presets.map((p) => (
              <DropdownMenuItem key={p.id} data-testid={`preset-item-${p.id}`}
                onClick={() => { onApply(p.filters || {}); toast.success(`Applied preset "${p.name}"`); }}
                className="cursor-pointer group">
                <Bookmark className="h-3.5 w-3.5 mr-2 text-[hsl(var(--primary))]" />
                <span className="flex-1 truncate">{p.name}</span>
                <button data-testid={`preset-delete-${p.id}`}
                  onClick={(e) => remove(e, p)}
                  title="Delete preset"
                  className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-600 p-0.5 rounded">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuItem>
            ))
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="preset-save-button" onClick={() => setSaveOpen(true)}
            className="cursor-pointer text-[hsl(var(--primary))] focus:text-[hsl(var(--primary))]">
            <BookmarkPlus className="h-4 w-4 mr-2" /> Save current filters...
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save Filter Preset</DialogTitle>
            <DialogDescription>Saves your current search, filters, and sorting for quick reuse.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Preset name</Label>
            <Input data-testid="preset-name-input" placeholder="e.g. Active AR engagements" value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button data-testid="preset-save-submit" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Preset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
